import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, PlayCircle, FileText, HelpCircle, File, CheckCircle, Eye, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
const CurriculumContainer = styled.div`
  margin-top: 32px;
`;
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const SummaryInfo = styled.span`
  font-size: 0.85rem;
  color: var(--theme-text-muted);
`;
const ExpandToggleButton = styled.button`
  background: none;
  border: none;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  color: var(--theme-primary);
  font-weight: var(--theme-typography-font-weight-medium, 500);
  font-size: 0.85rem;
  padding: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;
const Section = styled.div`
  border: 1px solid var(--theme-border);
  html.dark &, body.vscode-dark & {
    border-color: var(--theme-border-light);
  }
  margin-bottom: -1px;
  background-color: var(--my-card-background);

  &:first-of-type {
      border-top-left-radius: var(--theme-radius-md, 4px);
      border-top-right-radius: var(--theme-radius-md, 4px);
      overflow: hidden;
  }
  &:last-of-type {
      border-bottom-left-radius: var(--theme-radius-md, 4px);
      border-bottom-right-radius: var(--theme-radius-md, 4px);
      margin-bottom: 0;
  }
`;
const SectionHeader = styled.button`
  background-color: var(--theme-paper-light);
  border: none;
  padding: 16px;
  width: 100%;
  text-align: left;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
  color: var(--theme-text);

  &:hover {
    background-color: var(--theme-paper-hover);
  }
`;
const LectureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: var(--my-card-background);
  overflow: hidden;
  max-height: ${(props)=>props.isExpanded ? '1000px' : '0'};
  transition: max-height 0.3s ease-in-out;
`;
const LectureItem = styled.li`
  padding: 10px 16px 10px 40px;
  border-top: 1px solid var(--theme-border-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  position: relative;
  cursor: pointer;
  opacity: ${({ isCompleted })=>isCompleted ? 0.7 : 1};
  color: var(--theme-text);

  &:hover {
    background-color: var(--theme-paper-hover);
    opacity: 1;
  }
`;
const LectureIconWrapper = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--theme-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;

  .completed-icon {
    color: var(--theme-success);
  }

  svg {
      width: 16px;
      height: 16px;
  }
`;
const LectureTitle = styled.span`
  color: var(--theme-text);
`;
const LectureDuration = styled.span`
    color: var(--theme-text-muted);
    font-size: 0.85rem;
    flex-shrink: 0;
    margin-left: 16px;
`;
const PreviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: #10b981;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 8px;

  svg {
    width: 12px;
    height: 12px;
  }
`;
const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--theme-text-muted);
  font-size: 0.75rem;
  margin-left: 8px;

  svg {
    width: 12px;
    height: 12px;
  }
`;
const CurriculumTitle = styled.h2`
  color: var(--theme-text);
  margin-bottom: 16px;
`;
const formatDuration = (duration)=>{
    if (!duration || duration <= 0) return '';
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor(duration % 3600 / 60);
    const remainingSeconds = duration % 60;
    let formatted = '';
    if (hours > 0) formatted += `${hours}시간 `;
    if (minutes > 0) formatted += `${minutes}분 `;
    if (hours === 0 && minutes === 0 && remainingSeconds > 0) formatted = `${remainingSeconds}초`;
    return formatted.trim();
};
/**
 * 강의 커리큘럼을 표시하는 컴포넌트
 * 섹션별로 강의 목록을 표시하고 펼치기/접기 기능 제공
 */ const CourseCurriculum = ({ curriculum, isOwned, isInstructor = false, courseId, onLectureClick, checkIsOwned })=>{
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [isAllExpanded, setIsAllExpanded] = useState(false);
    const toKey = (value)=>String(value ?? '');
    const toggleSection = (sectionId)=>{
        const key = toKey(sectionId);
        setExpandedSections((prevExpanded)=>{
            const newExpanded = new Set(prevExpanded);
            if (newExpanded.has(key)) {
                newExpanded.delete(key);
            } else {
                newExpanded.add(key);
            }
            setIsAllExpanded(newExpanded.size === curriculum.length);
            return newExpanded;
        });
    };
    const expandAll = ()=>{
        const allSectionIds = new Set(curriculum.map((section)=>toKey(section.id)));
        setExpandedSections(allSectionIds);
        setIsAllExpanded(true);
    };
    const collapseAll = ()=>{
        setExpandedSections(new Set());
        setIsAllExpanded(false);
    };
    const { totalLectures, totalDurationSeconds } = useMemo(()=>{
        let lectureCount = 0;
        let durationSeconds = 0;
        curriculum.forEach((section)=>{
            lectureCount += section.lectures?.length ?? 0;
            section.lectures?.forEach((lecture)=>{
                durationSeconds += lecture.duration ?? 0;
            });
        });
        return {
            totalLectures: lectureCount,
            totalDurationSeconds: durationSeconds
        };
    }, [
        curriculum
    ]);
    const displayTotalDuration = formatDuration(totalDurationSeconds);
    const getLectureIcon = (contentType)=>{
        switch((contentType ?? '').toLowerCase()){
            case 'video':
                return /*#__PURE__*/ _jsx(PlayCircle, {
                    "aria-hidden": "true"
                });
            case 'markdown':
                return /*#__PURE__*/ _jsx(FileText, {
                    "aria-hidden": "true"
                });
            case 'quiz':
                return /*#__PURE__*/ _jsx(HelpCircle, {
                    "aria-hidden": "true"
                });
            default:
                return /*#__PURE__*/ _jsx(File, {
                    "aria-hidden": "true"
                });
        }
    };
    const handleLectureClick = (lectureId, isPreview, sectionIndex, lectureIndex)=>{
        // 미리보기 레슨은 권한 체크 우회
        // checkIsOwned 콜백이 있으면 사용 (최신 상태), 없으면 prop 사용
        const currentIsOwned = checkIsOwned ? checkIsOwned() : isOwned;
        // 강의 작성자는 수강 신청 없이도 접근 가능
        if (!isPreview && !currentIsOwned && !isInstructor) {
            toast.error("강의를 수강하려면 먼저 등록해주세요.");
            return;
        }
        if (!courseId) {
            console.error("Course ID is missing, cannot navigate to lecture.");
            toast.error("강좌 정보를 찾을 수 없어 이동할 수 없습니다.");
            return;
        }
        const lectureKey = toKey(lectureId);
        console.log(`Navigating to Course ID: ${courseId}, Lecture ID: ${lectureKey}, isPreview: ${isPreview}, sectionIndex: ${sectionIndex}, lectureIndex: ${lectureIndex}`);
        // Use callback if provided (for Next.js routing), otherwise try window.location
        if (onLectureClick) {
            onLectureClick(courseId, lectureKey, sectionIndex, lectureIndex);
        } else {
            // Fallback to direct navigation
            window.location.href = `/learn/course/${courseId}/lecture/${lectureKey}`;
        }
    };
    useEffect(()=>{
        if (curriculum.length === 0) {
            setExpandedSections(new Set());
            setIsAllExpanded(false);
            return;
        }
        const firstSectionId = toKey(curriculum[0].id);
        setExpandedSections(new Set([
            firstSectionId
        ]));
        setIsAllExpanded(curriculum.length === 1);
    }, [
        curriculum
    ]);
    if (!curriculum || curriculum.length === 0) {
        return /*#__PURE__*/ _jsxs(CurriculumContainer, {
            children: [
                /*#__PURE__*/ _jsx(CurriculumTitle, {
                    children: "강의 콘텐츠"
                }),
                /*#__PURE__*/ _jsx("div", {
                    children: "커리큘럼 정보가 없습니다."
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(CurriculumContainer, {
        children: [
            /*#__PURE__*/ _jsx(CurriculumTitle, {
                children: "강의 콘텐츠"
            }),
            /*#__PURE__*/ _jsxs(HeaderRow, {
                children: [
                    /*#__PURE__*/ _jsx(SummaryInfo, {
                        children: `총 ${curriculum.length}개 섹션 • ${totalLectures}개 강의 ${displayTotalDuration ? `• 전체 ${displayTotalDuration}` : ''}`
                    }),
                    /*#__PURE__*/ _jsx(ExpandToggleButton, {
                        onClick: isAllExpanded ? collapseAll : expandAll,
                        children: isAllExpanded ? '모든 섹션 접기' : '모든 섹션 펼치기'
                    })
                ]
            }),
            curriculum.map((section)=>{
                const sectionKey = toKey(section.id);
                const isExpanded = expandedSections.has(sectionKey);
                return /*#__PURE__*/ _jsxs(Section, {
                    children: [
                        /*#__PURE__*/ _jsxs(SectionHeader, {
                            onClick: ()=>toggleSection(section.id),
                            "aria-expanded": isExpanded,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: section.title
                                }),
                                isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {})
                            ]
                        }),
                        /*#__PURE__*/ _jsx(LectureList, {
                            isExpanded: isExpanded,
                            children: Array.isArray(section.lectures) && section.lectures.map((lecture, lectureIdx)=>{
                                const isCompleted = lecture.isCompleted === true;
                                const isPreview = lecture.isPreview === true;
                                const lectureKey = toKey(lecture.id);
                                return /*#__PURE__*/ _jsxs(LectureItem, {
                                    onClick: ()=>handleLectureClick(lecture.id, isPreview, lecture.sectionIndex, lecture.lectureIndex),
                                    isCompleted: isCompleted,
                                    "data-testid": `curriculum-lecture-${section.order ?? 0}-${lectureIdx}`,
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            style: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                flex: 1
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsxs(LectureIconWrapper, {
                                                    children: [
                                                        isCompleted && /*#__PURE__*/ _jsx(CheckCircle, {
                                                            className: "completed-icon",
                                                            "aria-label": "완료됨"
                                                        }),
                                                        !isCompleted && getLectureIcon(lecture.contentType)
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(LectureTitle, {
                                                    children: lecture.title
                                                }),
                                                isPreview && /*#__PURE__*/ _jsxs(PreviewBadge, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Eye, {}),
                                                        "무료 미리보기"
                                                    ]
                                                }),
                                                !isPreview && !isOwned && !isInstructor && /*#__PURE__*/ _jsx(LockedBadge, {
                                                    children: /*#__PURE__*/ _jsx(Lock, {})
                                                })
                                            ]
                                        }),
                                        lecture.contentType === 'VIDEO' && lecture.duration && /*#__PURE__*/ _jsx(LectureDuration, {
                                            children: formatDuration(lecture.duration)
                                        })
                                    ]
                                }, lectureKey);
                            })
                        })
                    ]
                }, sectionKey);
            })
        ]
    });
};
export default CourseCurriculum;
